import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import axios from 'axios'
import { format, parseISO, addMonths, startOfMonth, endOfMonth } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Switch } from '../components/ui/switch'
import { Badge } from '../components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { BottomSheet, PickerSheet } from '../components/BottomSheet'
import { toast } from 'sonner'
import { cn } from '../lib/utils'
import { useLanguage } from '../context/LanguageContext'
import { CollaboratorsModal } from '../components/CollaboratorsModal'
import AntIndicator from '../components/AntIndicator'
import AntExpensesModal from '../components/AntExpensesModal'
import InvestmentIndicator from '../components/InvestmentIndicator'
import InvestmentModal from '../components/InvestmentModal'
import { 
  QuickAddFinanceCategoryButton, 
  QuickAddFinanceSubcategoryButton 
} from '../components/modals/FinanceCategoryModal'
import { 
  Plus, ArrowUpCircle, ArrowDownCircle, Filter, X, Users, RefreshCw,
  Wallet, TrendingUp, Home, Utensils, Car, Heart, GraduationCap, Gamepad2,
  CreditCard, Shirt, BadgeDollarSign, PiggyBank, Gift,
  MoreHorizontal, Briefcase, Building, Zap, Circle, Trash2, ChevronDown, AlertCircle, AlertTriangle,
  Calendar, Check, Clock, CreditCard as CardIcon, Pencil
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Icon mapping for categories
const iconMap = {
  Home, Utensils, Car, Heart, GraduationCap, Gamepad2,
  CreditCard, Shirt, BadgeDollarSign, TrendingUp, PiggyBank, Gift,
  MoreHorizontal, Briefcase, Building, Zap, Circle, Wallet
}

const CURRENCIES = ['MXN', 'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'BRL', 'ARS', 'COP', 'CLP', 'PEN']

// Haptic feedback helper
const triggerHaptic = () => {
  if (navigator.vibrate) {
    navigator.vibrate(10)
  }
}

export default function FinanzasPage() {
  const { t, locale, formatCurrency: formatCurrencyIntl } = useLanguage()
  const [transactions, setTransactions] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [showQuickAdd, setShowQuickAdd] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [summary, setSummary] = useState({ income: 0, expense: 0, balance: 0, transaction_count: 0 })
  
  // Initialize filters with current month by default
  const [filters, setFilters] = useState(() => {
    const now = new Date()
    const firstDay = startOfMonth(now)
    const lastDay = endOfMonth(now)
    return {
      tipo: '',
      categoria_id: '', // New: filter by category
      startDate: format(firstDay, 'yyyy-MM-dd'),
      endDate: format(lastDay, 'yyyy-MM-dd')
    }
  })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, transaction: null })
  const [showCollaborators, setShowCollaborators] = useState(false)
  const [editingTransaction, setEditingTransaction] = useState(null)
  const [showAntExpenses, setShowAntExpenses] = useState(false)
  const [showInvestments, setShowInvestments] = useState(false)

  const dateLocale = locale === 'es-MX' ? es : enUS
  
  // Current month for ant expense calculations
  const currentMonth = format(new Date(), 'yyyy-MM')
  
  // Check if current filter is the current month (for period label)
  const isCurrentMonth = useMemo(() => {
    const now = new Date()
    const firstDay = format(startOfMonth(now), 'yyyy-MM-dd')
    const lastDay = format(endOfMonth(now), 'yyyy-MM-dd')
    return filters.startDate === firstDay && filters.endDate === lastDay
  }, [filters.startDate, filters.endDate])
  
  // Period label for display
  const periodLabel = useMemo(() => {
    if (!filters.startDate && !filters.endDate) {
      return locale === 'es-MX' ? 'Todo el tiempo' : 'All time'
    }
    
    if (filters.startDate && filters.endDate) {
      const start = parseISO(filters.startDate)
      const end = parseISO(filters.endDate)
      
      // Check if it's a full month
      const startMonth = startOfMonth(start)
      const endMonth = endOfMonth(start)
      
      if (format(start, 'yyyy-MM-dd') === format(startMonth, 'yyyy-MM-dd') &&
          format(end, 'yyyy-MM-dd') === format(endMonth, 'yyyy-MM-dd')) {
        // It's a full month
        return format(start, 'MMMM yyyy', { locale: dateLocale })
      }
      
      // Custom range
      return `${format(start, 'dd MMM', { locale: dateLocale })} - ${format(end, 'dd MMM yyyy', { locale: dateLocale })}`
    }
    
    return locale === 'es-MX' ? 'Periodo personalizado' : 'Custom period'
  }, [filters.startDate, filters.endDate, dateLocale, locale])
  
  // Helper to set filter to a specific month
  const setMonthFilter = (date) => {
    const firstDay = startOfMonth(date)
    const lastDay = endOfMonth(date)
    setFilters(prev => ({
      ...prev,
      startDate: format(firstDay, 'yyyy-MM-dd'),
      endDate: format(lastDay, 'yyyy-MM-dd')
    }))
  }
  
  // Navigate to previous/next month
  const goToPreviousMonth = () => {
    const currentStart = filters.startDate ? parseISO(filters.startDate) : new Date()
    setMonthFilter(addMonths(currentStart, -1))
  }
  
  const goToNextMonth = () => {
    const currentStart = filters.startDate ? parseISO(filters.startDate) : new Date()
    setMonthFilter(addMonths(currentStart, 1))
  }
  
  // Reset to current month
  const resetToCurrentMonth = () => {
    setMonthFilter(new Date())
  }

  const PAYMENT_METHODS = [
    { value: 'Efectivo', label: t('finance.cash') },
    { value: 'Débito', label: t('finance.debit') },
    { value: 'Crédito', label: t('finance.credit') },
    { value: 'Transferencia', label: t('finance.transfer') },
    { value: 'Otro', label: t('finance.other') },
  ]

  const fetchTransactions = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true)
    try {
      // Build URL for transactions (with higher limit for display)
      let txUrl = `${API}/finance/transactions?limit=200`
      if (filters.startDate) txUrl += `&start_date=${filters.startDate}`
      if (filters.endDate) txUrl += `&end_date=${filters.endDate}`
      if (filters.tipo) txUrl += `&tipo=${filters.tipo}`
      if (filters.categoria_id) txUrl += `&categoria_id=${filters.categoria_id}`
      
      // Build URL for summary (no limit, accurate totals)
      let summaryUrl = `${API}/finance/summary?`
      if (filters.startDate) summaryUrl += `start_date=${filters.startDate}&`
      if (filters.endDate) summaryUrl += `end_date=${filters.endDate}`
      
      // Fetch both in parallel
      const [txResponse, summaryResponse] = await Promise.all([
        axios.get(txUrl),
        axios.get(summaryUrl)
      ])
      
      setTransactions(txResponse.data)
      setSummary(summaryResponse.data)
    } catch (error) {
      console.error('Error fetching transactions:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [filters])

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/finance/categories`)
      setCategories(response.data)
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }, [])

  // Manual refresh handler
  const handleRefresh = () => {
    fetchTransactions(true)
    fetchCategories()
  }

  useEffect(() => {
    fetchTransactions()
    fetchCategories()
    
    // Auto-refresh when user comes back to the page
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchTransactions(false) // Silent refresh
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [fetchTransactions, fetchCategories])

  const handleDeleteTransaction = async (id) => {
    try {
      await axios.delete(`${API}/finance/transactions/${id}`)
      toast.success(t('finance.transactionDeleted'))
      setDeleteDialog({ open: false, transaction: null })
      fetchTransactions()
    } catch (error) {
      toast.error(t('finance.errorDeleting'))
    }
  }

  const openDeleteDialog = (tx) => {
    triggerHaptic()
    setDeleteDialog({ open: true, transaction: tx })
  }

  // Summary is now fetched from backend (no local calculation)
  // This ensures accurate totals regardless of pagination limits
  
  // Calculate ant expenses (gasto hormiga) for current month
  const antExpenseSummary = useMemo(() => {
    const now = new Date()
    const monthStart = startOfMonth(now)
    const monthEnd = endOfMonth(now)
    
    let antExpenseAmount = 0
    let totalExpensesThisMonth = 0
    
    transactions.forEach(tx => {
      if (tx.tipo === 'expense') {
        const txDate = parseISO(tx.fecha)
        if (txDate >= monthStart && txDate <= monthEnd) {
          totalExpensesThisMonth += tx.monto
          if (tx.es_desperdicio) {
            antExpenseAmount += tx.monto
          }
        }
      }
    })
    
    return { antExpenseAmount, totalExpensesThisMonth }
  }, [transactions])
  
  // Calculate investments for current month
  const investmentSummary = useMemo(() => {
    const now = new Date()
    const monthStart = startOfMonth(now)
    const monthEnd = endOfMonth(now)
    
    let investmentAmount = 0
    let totalExpensesThisMonth = 0
    
    transactions.forEach(tx => {
      if (tx.tipo === 'expense') {
        const txDate = parseISO(tx.fecha)
        if (txDate >= monthStart && txDate <= monthEnd) {
          if (tx.es_inversion) {
            investmentAmount += tx.monto
          } else {
            // Regular expenses (excluding investments)
            totalExpensesThisMonth += tx.monto
          }
        }
      }
    })
    
    return { investmentAmount, totalExpensesThisMonth }
  }, [transactions])

  const formatCurrency = (amount, currency = 'MXN') => {
    return formatCurrencyIntl(amount, currency)
  }

  if (loading) {
    return (
      <div className="p-4 md:p-8 pb-safe flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 pb-safe max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-1">{t('finance.title')}</h1>
          <p className="text-muted-foreground text-sm">{locale === 'es-MX' ? 'Controla tus ingresos y gastos' : 'Track your income and expenses'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowCollaborators(true)}
            className="rounded-full"
            data-testid="share-btn"
            title={locale === 'es-MX' ? 'Compartir' : 'Share'}
          >
            <Users className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-full"
            data-testid="refresh-finance-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            className="rounded-full"
            data-testid="filter-btn"
          >
            <Filter className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => {
              triggerHaptic()
              setShowQuickAdd(true)
            }}
            size="sm"
            className="rounded-full h-10 px-4 bg-blue-500 hover:bg-blue-600"
            data-testid="quick-add-btn"
          >
            <Plus className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">{t('tasks.new')}</span>
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="flex items-center justify-between mb-4 bg-secondary/30 rounded-xl p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={goToPreviousMonth}
          className="rounded-full"
          data-testid="prev-month-btn"
        >
          <ChevronDown className="w-4 h-4 rotate-90" />
        </Button>
        
        <button
          onClick={() => setShowFilters(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-secondary/50 transition-colors"
          data-testid="period-selector-btn"
        >
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium capitalize">{periodLabel}</span>
          {!isCurrentMonth && (
            <Badge variant="outline" className="text-xs ml-1">
              {locale === 'es-MX' ? 'Filtrado' : 'Filtered'}
            </Badge>
          )}
        </button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={goToNextMonth}
          className="rounded-full"
          data-testid="next-month-btn"
        >
          <ChevronDown className="w-4 h-4 -rotate-90" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="bg-emerald-500/10 border-emerald-500/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpCircle className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground">{t('finance.income')}</span>
            </div>
            <p className="text-sm font-bold text-emerald-500">{formatCurrency(summary.income)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-rose-500/10 border-rose-500/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownCircle className="w-4 h-4 text-rose-500" />
              <span className="text-xs text-muted-foreground">{t('finance.expense')}</span>
            </div>
            <p className="text-sm font-bold text-rose-500">{formatCurrency(summary.expense)}</p>
          </CardContent>
        </Card>
        
        <Card className={cn(
          "border-sky-500/20",
          summary.income - summary.expense >= 0 ? "bg-sky-500/10" : "bg-amber-500/10"
        )}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-sky-500" />
              <span className="text-xs text-muted-foreground">{t('finance.balance')}</span>
            </div>
            <p className={cn(
              "text-sm font-bold",
              summary.income - summary.expense >= 0 ? "text-sky-500" : "text-amber-500"
            )}>
              {formatCurrency(summary.income - summary.expense)}
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Indicators Row - Ant Expense & Investment - Side by Side */}
      {(antExpenseSummary.antExpenseAmount > 0 || investmentSummary.investmentAmount > 0) && (
        <div className="grid grid-cols-2 gap-3 mb-6 px-1">
          {antExpenseSummary.antExpenseAmount > 0 ? (
            <AntIndicator
              antExpenseAmount={antExpenseSummary.antExpenseAmount}
              totalExpenses={antExpenseSummary.totalExpensesThisMonth}
              onClick={() => setShowAntExpenses(true)}
              compact
            />
          ) : (
            <div /> /* Empty placeholder to maintain grid */
          )}
          {investmentSummary.investmentAmount > 0 ? (
            <InvestmentIndicator
              investmentAmount={investmentSummary.investmentAmount}
              totalExpenses={investmentSummary.totalExpensesThisMonth}
              onClick={() => setShowInvestments(true)}
              compact
            />
          ) : (
            <div /> /* Empty placeholder to maintain grid */
          )}
        </div>
      )}

      {/* Filters (collapsible) */}
      {showFilters && (
        <Card className="mb-6">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('finance.filters') || 'Filtros'}</span>
              {!isCurrentMonth && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetToCurrentMonth}
                  className="rounded-full text-xs"
                  data-testid="reset-month-btn"
                >
                  <Calendar className="w-3 h-3 mr-1" />
                  {locale === 'es-MX' ? 'Mes actual' : 'Current month'}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">{t('finance.startDate')}</Label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className="h-10"
                />
              </div>
              <div>
                <Label className="text-xs">{t('finance.endDate')}</Label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className="h-10"
                />
              </div>
            </div>
            
            {/* Type Filter */}
            <div>
              <Label className="text-xs mb-2 block">{locale === 'es-MX' ? 'Tipo' : 'Type'}</Label>
              <div className="flex gap-2">
                <Button
                  variant={filters.tipo === '' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilters(prev => ({ ...prev, tipo: '' }))}
                  className="rounded-full"
                >
                  {t('common.all')}
                </Button>
                <Button
                  variant={filters.tipo === 'income' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilters(prev => ({ ...prev, tipo: 'income' }))}
                  className="rounded-full"
                >
                  <ArrowUpCircle className="w-4 h-4 mr-1" /> {t('finance.income')}
                </Button>
                <Button
                  variant={filters.tipo === 'expense' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilters(prev => ({ ...prev, tipo: 'expense' }))}
                  className="rounded-full"
                >
                  <ArrowDownCircle className="w-4 h-4 mr-1" /> {t('finance.expense')}
                </Button>
              </div>
            </div>
            
            {/* Category Filter */}
            <div>
              <Label className="text-xs mb-2 block">{locale === 'es-MX' ? 'Categoría' : 'Category'}</Label>
              <Select
                value={filters.categoria_id || 'all'}
                onValueChange={(value) => setFilters(prev => ({ ...prev, categoria_id: value === 'all' ? '' : value }))}
              >
                <SelectTrigger className="h-10" data-testid="category-filter-select">
                  <SelectValue placeholder={locale === 'es-MX' ? 'Todas las categorías' : 'All categories'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {locale === 'es-MX' ? 'Todas las categorías' : 'All categories'}
                  </SelectItem>
                  {categories
                    .filter(cat => !filters.tipo || cat.type === filters.tipo || cat.type === 'both')
                    .map(cat => {
                      const CatIcon = iconMap[cat.icon] || Circle
                      return (
                        <SelectItem key={cat.id} value={cat.id}>
                          <div className="flex items-center gap-2">
                            <CatIcon className="w-4 h-4" style={{ color: cat.color }} />
                            <span>{cat.name}</span>
                          </div>
                        </SelectItem>
                      )
                    })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Clear filters button */}
            {(filters.tipo || filters.categoria_id) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters(prev => ({ ...prev, tipo: '', categoria_id: '' }))}
                className="w-full text-muted-foreground"
              >
                <X className="w-4 h-4 mr-1" />
                {locale === 'es-MX' ? 'Limpiar filtros' : 'Clear filters'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Transactions List */}
      <div className="space-y-3 mb-24">
        {transactions.length === 0 ? (
          <Card className="p-8 text-center">
            <Wallet className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">{t('finance.noTransactions')}</p>
            <p className="text-sm text-muted-foreground mt-1">{t('finance.createFirst')}</p>
          </Card>
        ) : (
          transactions.map((tx) => {
            const IconComponent = iconMap[tx.categoria_icon] || Circle
            return (
              <Card 
                key={tx.id} 
                className="overflow-hidden cursor-pointer hover:bg-accent/50 transition-colors" 
                data-testid={`transaction-${tx.id}`}
                onClick={() => setEditingTransaction(tx)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${tx.categoria_color}20` }}
                    >
                      <IconComponent className="w-5 h-5" style={{ color: tx.categoria_color }} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{tx.categoria_nombre}</p>
                        {tx.es_msi && (
                          <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20" data-testid={`msi-badge-${tx.id}`}>
                            <CardIcon className="w-3 h-3 mr-1" />
                            MSI {tx.msi_meses}m
                          </Badge>
                        )}
                        {tx.es_inversion && (
                          <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                            {t('finance.isInvestment')}
                          </Badge>
                        )}
                        {tx.es_desperdicio && (
                          <Badge variant="outline" className="text-xs bg-rose-500/10 text-rose-500 border-rose-500/20">
                            {t('finance.isWaste')}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{format(parseISO(tx.fecha), 'd MMM', { locale: dateLocale })}</span>
                        <span>•</span>
                        <span>{tx.metodo}</span>
                        {tx.es_msi && tx.msi_pagados !== null && (
                          <>
                            <span>•</span>
                            <span className="text-primary font-medium">
                              {tx.msi_pagados}/{tx.msi_meses} pagados
                            </span>
                          </>
                        )}
                        {tx.notas && !tx.es_msi && (
                          <>
                            <span>•</span>
                            <span className="truncate">{tx.notas}</span>
                          </>
                        )}
                      </div>
                      {/* MSI Progress bar */}
                      {tx.es_msi && tx.msi_meses && (
                        <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${((tx.msi_pagados || 0) / tx.msi_meses) * 100}%` }}
                          />
                        </div>
                      )}
                    </div>
                    
                    <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                      <p className={cn(
                        "font-bold",
                        tx.tipo === 'income' ? 'text-emerald-500' : 'text-rose-500'
                      )}>
                        {tx.tipo === 'income' ? '+' : '-'}{formatCurrency(tx.monto, tx.moneda)}
                      </p>
                      {tx.es_msi && tx.msi_monto_mensual && (
                        <span className="text-xs text-muted-foreground">
                          ${tx.msi_monto_mensual.toFixed(2)}/mes
                        </span>
                      )}
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setEditingTransaction(tx)
                          }}
                          className="p-2 rounded-full hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                          data-testid={`edit-tx-${tx.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            openDeleteDialog(tx)
                          }}
                          className="p-2 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                          data-testid={`delete-tx-${tx.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })
        )}
      </div>

      {/* Bottom Sheet Modal for Mobile */}
      {showQuickAdd && (
        <BottomSheetModal 
          onClose={() => setShowQuickAdd(false)}
          categories={categories}
          onSuccess={() => {
            setShowQuickAdd(false)
            fetchTransactions()
          }}
          onCategoriesRefresh={fetchCategories}
          t={t}
          locale={locale}
        />
      )}

      {/* Edit Transaction Modal */}
      {editingTransaction && (
        <BottomSheetModal 
          onClose={() => setEditingTransaction(null)}
          categories={categories}
          editTransaction={editingTransaction}
          onSuccess={() => {
            setEditingTransaction(null)
            fetchTransactions()
          }}
          onCategoriesRefresh={fetchCategories}
          t={t}
          locale={locale}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, transaction: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {t('finance.deleteConfirm')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.transaction && (
                <>
                  {locale === 'es-MX' 
                    ? <>Estás a punto de eliminar esta transacción de <strong>{formatCurrency(deleteDialog.transaction.monto, deleteDialog.transaction.moneda)}</strong>.</>
                    : <>You are about to delete this transaction of <strong>{formatCurrency(deleteDialog.transaction.monto, deleteDialog.transaction.moneda)}</strong>.</>
                  }
                  {' '}{t('finance.deleteWarning')}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('finance.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteDialog.transaction && handleDeleteTransaction(deleteDialog.transaction.id)}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t('finance.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Collaborators Modal */}
      <CollaboratorsModal 
        open={showCollaborators} 
        onOpenChange={setShowCollaborators}
        module="finance"
      />
      
      {/* Ant Expenses Modal */}
      <AntExpensesModal
        isOpen={showAntExpenses}
        onClose={() => setShowAntExpenses(false)}
        month={currentMonth}
        onTransactionUpdated={() => fetchTransactions(true)}
      />
      
      {/* Investment Modal */}
      <InvestmentModal
        isOpen={showInvestments}
        onClose={() => setShowInvestments(false)}
        month={currentMonth}
        onTransactionUpdated={() => fetchTransactions(true)}
      />
    </div>
  )
}

// Bottom Sheet Modal Component - Optimized for iOS Safari
function BottomSheetModal({ onClose, categories, onSuccess, onCategoriesRefresh, editTransaction = null, t, locale }) {
  const [isVisible, setIsVisible] = useState(false)
  const isEditing = !!editTransaction
  
  // Payment methods with translations
  const PAYMENT_METHODS = [
    { value: 'Efectivo', label: locale === 'es-MX' ? 'Efectivo' : 'Cash' },
    { value: 'Débito', label: locale === 'es-MX' ? 'Débito' : 'Debit' },
    { value: 'Crédito', label: locale === 'es-MX' ? 'Crédito' : 'Credit' },
    { value: 'Transferencia', label: locale === 'es-MX' ? 'Transferencia' : 'Transfer' },
    { value: 'Otro', label: locale === 'es-MX' ? 'Otro' : 'Other' },
  ]
  
  // Get today's date in local timezone (not UTC)
  const getLocalDate = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  
  // Initialize form data - either from editTransaction or defaults
  const getInitialFormData = () => {
    if (editTransaction) {
      return {
        tipo: editTransaction.tipo || 'expense',
        monto: editTransaction.monto?.toString() || '',
        categoria_principal: editTransaction.categoria_principal || '',
        subcategoria_id: editTransaction.subcategoria_id || '',
        fecha: editTransaction.fecha || getLocalDate(),
        moneda: editTransaction.moneda || 'MXN',
        metodo: editTransaction.metodo || 'Efectivo',
        es_inversion: editTransaction.es_inversion || false,
        es_desperdicio: editTransaction.es_desperdicio || false,
        notas: editTransaction.notas || '',
        // MSI fields
        es_msi: editTransaction.es_msi || false,
        msi_meses: editTransaction.msi_meses || 6,
        msi_fecha_primer_cargo: editTransaction.msi_fecha_primer_cargo || ''
      }
    }
    return {
      tipo: 'expense',
      monto: '',
      categoria_principal: '',
      subcategoria_id: '',
      fecha: getLocalDate(),
      moneda: 'MXN',
      metodo: 'Efectivo',
      es_inversion: false,
      es_desperdicio: false,
      notas: '',
      // MSI fields
      es_msi: false,
      msi_meses: 6,
      msi_fecha_primer_cargo: ''
    }
  }
  
  const [formData, setFormData] = useState(getInitialFormData)
  const [loading, setLoading] = useState(false)
  const [showCategoryPicker, setShowCategoryPicker] = useState(false)
  const [showSubcategoryPicker, setShowSubcategoryPicker] = useState(false)
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false)
  const [showMethodPicker, setShowMethodPicker] = useState(false)
  const [showMSIMonthsPicker, setShowMSIMonthsPicker] = useState(false)
  const [errors, setErrors] = useState({})
  const montoInputRef = useRef(null)

  const MSI_MONTHS_OPTIONS = [3, 6, 9, 12, 18, 24]

  // Animation on mount
  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true))
    // Auto focus on monto input
    setTimeout(() => {
      montoInputRef.current?.focus()
    }, 300)
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 200)
  }

  const filteredCategories = categories.filter(cat => 
    cat.category_type === formData.tipo || cat.category_type === 'both'
  )

  const selectedCategory = categories.find(c => c.id === formData.categoria_principal)
  
  // Get subcategories for selected category
  const subcategories = selectedCategory?.subcategories?.filter(s => s.is_active) || []
  const selectedSubcategory = subcategories.find(s => s.id === formData.subcategoria_id)

  const validateForm = () => {
    const newErrors = {}
    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      newErrors.monto = 'Ingresa un monto válido'
    }
    if (!formData.categoria_principal) {
      newErrors.categoria = 'Selecciona una categoría'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      triggerHaptic()
      return
    }

    if (formData.es_inversion && formData.es_desperdicio) {
      toast.error('No puede ser inversión y desperdicio')
      return
    }
    
    setLoading(true)
    try {
      const payload = {
        ...formData,
        monto: parseFloat(formData.monto),
        // Include MSI data if enabled
        es_msi: formData.es_msi,
        msi_meses: formData.es_msi ? formData.msi_meses : null,
        msi_fecha_primer_cargo: formData.es_msi && formData.msi_fecha_primer_cargo 
          ? formData.msi_fecha_primer_cargo 
          : null
      }
      
      if (isEditing) {
        // Update existing transaction
        await axios.put(`${API}/finance/transactions/${editTransaction.id}`, payload)
        triggerHaptic()
        toast.success('Transacción actualizada ✓')
      } else {
        // Create new transaction
        await axios.post(`${API}/finance/transactions`, payload)
        triggerHaptic()
        
        if (formData.es_msi) {
          const monthlyAmount = (parseFloat(formData.monto) / formData.msi_meses).toFixed(2)
          toast.success(`MSI creado: $${monthlyAmount}/mes x ${formData.msi_meses} meses ✓`)
        } else {
          toast.success('Transacción guardada ✓')
        }
        
        // Trigger bonsai update for financial record (only on create)
        window.dispatchEvent(new CustomEvent('bonsai-update'))
      }
      
      onSuccess()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar')
    } finally {
      setLoading(false)
    }
  }

  // Check if MSI critical fields changed (for warning)
  const hasMSICriticalChanges = isEditing && editTransaction?.es_msi && (
    parseFloat(formData.monto) !== editTransaction.monto ||
    formData.msi_meses !== editTransaction.msi_meses ||
    formData.categoria_principal !== editTransaction.categoria_principal ||
    formData.msi_fecha_primer_cargo !== editTransaction.msi_fecha_primer_cargo
  )

  return (
    <>
      {/* Backdrop */}
      <div 
        className={cn(
          "fixed inset-0 bg-black/60 z-40 transition-opacity duration-200",
          isVisible ? "opacity-100" : "opacity-0"
        )}
        onClick={handleClose}
      />
      
      {/* Bottom Sheet */}
      <div 
        className={cn(
          "fixed left-0 right-0 bottom-0 z-50 bg-background rounded-t-3xl transition-transform duration-200 ease-out shadow-2xl",
          isVisible ? "translate-y-0" : "translate-y-full"
        )}
        style={{ 
          maxHeight: '85vh',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          marginTop: 'env(safe-area-inset-top, 0px)'
        }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-4 pb-2">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-4 border-b">
          <h2 className="text-lg font-bold">
            {isEditing ? t('finance.editTransaction') : t('finance.addTransaction')}
          </h2>
          <button onClick={handleClose} className="p-2 -mr-2 rounded-full hover:bg-secondary">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* MSI Warning Banner */}
        {hasMSICriticalChanges && editTransaction?.msi_pagados > 0 && (
          <div className="mx-4 mt-2 p-3 rounded-lg bg-amber-500/20 border border-amber-500/30">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-700 dark:text-amber-400">
                  Esta transacción MSI tiene {editTransaction.msi_pagados} cargo(s) pagado(s)
                </p>
                <p className="text-muted-foreground mt-1">
                  Los cargos pagados se mantendrán intactos. Solo se recalcularán los cargos pendientes.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form Content - Scrollable */}
        <div className="overflow-y-auto px-1" style={{ maxHeight: 'calc(85vh - 120px)' }}>
          <form onSubmit={handleSubmit} className="p-4 pt-2 space-y-4">
            {/* Type Toggle - Segmented Control Style */}
            <div className="flex gap-2 p-1 bg-secondary/50 rounded-full">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  triggerHaptic()
                  setFormData(prev => ({ ...prev, tipo: 'expense', categoria_principal: '' }))
                }}
                className={cn(
                  "flex-1 rounded-full h-11 transition-all font-medium",
                  formData.tipo === 'expense' 
                    ? "bg-rose-500 hover:bg-rose-500 text-white shadow-md" 
                    : "hover:bg-transparent text-muted-foreground"
                )}
              >
                <ArrowDownCircle className="w-4 h-4 mr-2" /> {t('finance.expense')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  triggerHaptic()
                  setFormData(prev => ({ ...prev, tipo: 'income', categoria_principal: '', es_inversion: false, es_desperdicio: false }))
                }}
                className={cn(
                  "flex-1 rounded-full h-11 transition-all font-medium",
                  formData.tipo === 'income' 
                    ? "bg-emerald-500 hover:bg-emerald-500 text-white shadow-md" 
                    : "hover:bg-transparent text-muted-foreground"
                )}
              >
                <ArrowUpCircle className="w-4 h-4 mr-2" /> {t('finance.income')}
              </Button>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label>{t('finance.amount')} *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-lg">$</span>
                  <Input
                    ref={montoInputRef}
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.monto}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, monto: e.target.value }))
                      setErrors(prev => ({ ...prev, monto: null }))
                    }}
                    className={cn(
                      "pl-8 text-2xl font-bold h-14",
                      errors.monto && "border-destructive focus-visible:ring-destructive"
                    )}
                    data-testid="amount-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowCurrencyPicker(true)}
                  className="h-14 px-4 rounded-lg border bg-secondary flex items-center gap-2 font-medium"
                >
                  {formData.moneda}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              {errors.monto && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.monto}
                </p>
              )}
            </div>

            {/* Category - Custom Picker */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('finance.category')} *</Label>
                <QuickAddFinanceCategoryButton 
                  defaultType={formData.tipo}
                  onCategoryCreated={onCategoriesRefresh}
                />
              </div>
              {filteredCategories.length === 0 ? (
                <div className="p-4 rounded-lg border border-dashed text-center">
                  <p className="text-sm text-muted-foreground">
                    {locale === 'es-MX' ? 'No tienes categorías.' : 'No categories.'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {locale === 'es-MX' ? 'Crea una usando el botón + Nueva arriba' : 'Create one using the + New button above'}
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowCategoryPicker(true)}
                  className={cn(
                    "w-full h-14 px-4 rounded-lg border bg-background flex items-center justify-between",
                    errors.categoria && "border-destructive",
                    !selectedCategory && "text-muted-foreground"
                  )}
                >
                  {selectedCategory ? (
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${selectedCategory.color}20` }}
                      >
                        {(() => {
                          const Icon = iconMap[selectedCategory.icon] || Circle
                          return <Icon className="w-4 h-4" style={{ color: selectedCategory.color }} />
                        })()}
                      </div>
                      <span className="font-medium">{selectedCategory.name}</span>
                    </div>
                  ) : (
                    <span>{t('finance.selectCategory')}</span>
                  )}
                  <ChevronDown className="w-5 h-5" />
                </button>
              )}
              {errors.categoria && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.categoria}
                </p>
              )}
            </div>

            {/* Subcategory - Only show if selected category has subcategories */}
            {selectedCategory && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{locale === 'es-MX' ? 'Subcategoría' : 'Subcategory'}</Label>
                  <QuickAddFinanceSubcategoryButton
                    categoryId={selectedCategory?.id}
                    categoryName={selectedCategory?.name}
                    onSubcategoryCreated={onCategoriesRefresh}
                  />
                </div>
                {subcategories.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setShowSubcategoryPicker(true)}
                    className="w-full h-12 px-4 rounded-lg border bg-background flex items-center justify-between"
                    data-testid="subcategory-picker-btn"
                  >
                    {selectedSubcategory ? (
                      <span>{selectedSubcategory.name}</span>
                    ) : (
                      <span className="text-muted-foreground">
                        {locale === 'es-MX' ? 'Seleccionar subcategoría...' : 'Select subcategory...'}
                      </span>
                    )}
                    <ChevronDown className="w-5 h-5" />
                  </button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {locale === 'es-MX' ? 'No hay subcategorías. Crea una usando el botón + arriba.' : 'No subcategories. Create one using the + button above.'}
                  </p>
                )}
              </div>
            )}

            {/* Date */}
            <div className="space-y-2">
              <Label>{t('finance.date')}</Label>
              <Input
                type="date"
                value={formData.fecha}
                onChange={(e) => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
                className="h-12"
              />
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>{t('finance.paymentMethod')}</Label>
              <button
                type="button"
                onClick={() => setShowMethodPicker(true)}
                className="w-full h-12 px-4 rounded-lg border bg-background flex items-center justify-between"
              >
                <span>{formData.metodo}</span>
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>

            {/* Expense-specific fields */}
            {formData.tipo === 'expense' && (
              <div className="flex gap-6 py-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Switch
                    checked={formData.es_inversion}
                    onCheckedChange={(checked) => {
                      triggerHaptic()
                      setFormData(prev => ({ 
                        ...prev, 
                        es_inversion: checked,
                        es_desperdicio: checked ? false : prev.es_desperdicio
                      }))
                    }}
                  />
                  <span className="text-sm">{t('finance.isInvestment')}</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <Switch
                    checked={formData.es_desperdicio}
                    onCheckedChange={(checked) => {
                      triggerHaptic()
                      setFormData(prev => ({ 
                        ...prev, 
                        es_desperdicio: checked,
                        es_inversion: checked ? false : prev.es_inversion
                      }))
                    }}
                  />
                  <span className="text-sm">{locale === 'es-MX' ? 'Gasto hormiga' : 'Ant expense'}</span>
                </label>
              </div>
            )}

            {/* MSI Section - Only for expenses with credit card */}
            {formData.tipo === 'expense' && (
              <div className="space-y-3 p-3 rounded-lg bg-secondary/30 border border-border/50">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <CardIcon className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">
                      {locale === 'es-MX' ? '¿MSI (Meses Sin Intereses)?' : 'MSI (Interest-free months)?'}
                    </span>
                  </div>
                  <Switch
                    checked={formData.es_msi}
                    onCheckedChange={(checked) => {
                      triggerHaptic()
                      setFormData(prev => ({ 
                        ...prev, 
                        es_msi: checked,
                        metodo: checked ? 'Crédito' : prev.metodo,
                        // Set default first charge date (next month)
                        msi_fecha_primer_cargo: checked && !prev.msi_fecha_primer_cargo
                          ? (() => {
                              const txDate = new Date(prev.fecha || new Date())
                              const nextMonth = addMonths(txDate, 1)
                              return format(nextMonth, 'yyyy-MM-dd')
                            })()
                          : prev.msi_fecha_primer_cargo
                      }))
                    }}
                    data-testid="msi-switch"
                  />
                </label>

                {formData.es_msi && (
                  <div className="space-y-3 pt-2 border-t border-border/50">
                    {/* MSI Months Selector */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {locale === 'es-MX' ? 'Número de meses' : 'Number of months'}
                      </Label>
                      <button
                        type="button"
                        onClick={() => setShowMSIMonthsPicker(true)}
                        className="w-full h-11 px-4 rounded-lg border bg-background flex items-center justify-between"
                      >
                        <span className="font-medium">{formData.msi_meses} {locale === 'es-MX' ? 'meses' : 'months'}</span>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    {/* First Charge Date */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {locale === 'es-MX' ? 'Fecha primer cargo' : 'First charge date'}
                      </Label>
                      <Input
                        type="date"
                        value={formData.msi_fecha_primer_cargo}
                        onChange={(e) => setFormData(prev => ({ ...prev, msi_fecha_primer_cargo: e.target.value }))}
                        className="h-11"
                      />
                    </div>

                    {/* MSI Preview */}
                    {formData.monto && parseFloat(formData.monto) > 0 && (
                      <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            {locale === 'es-MX' ? 'Monto mensual:' : 'Monthly amount:'}
                          </span>
                          <span className="font-bold text-primary">
                            ${(parseFloat(formData.monto) / formData.msi_meses).toFixed(2)} {formData.moneda}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs mt-1">
                          <span className="text-muted-foreground">{locale === 'es-MX' ? 'Total:' : 'Total:'}</span>
                          <span className="font-medium">
                            ${parseFloat(formData.monto).toFixed(2)} {locale === 'es-MX' ? 'en' : 'in'} {formData.msi_meses} {locale === 'es-MX' ? 'pagos' : 'payments'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('finance.notes')} ({locale === 'es-MX' ? 'opcional' : 'optional'})</Label>
              <Input
                placeholder={locale === 'es-MX' ? 'Descripción breve...' : 'Brief description...'}
                value={formData.notas}
                onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
                className="h-12"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 rounded-full h-12"
              >
                {t('finance.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 rounded-full h-12"
                data-testid="save-transaction-btn"
              >
                {loading 
                  ? (locale === 'es-MX' ? 'Guardando...' : 'Saving...') 
                  : isEditing 
                    ? (locale === 'es-MX' ? 'Actualizar' : 'Update')
                    : t('finance.save')
                }
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Category Picker Sheet */}
      <PickerSheet
        title={t('finance.selectCategory')}
        isOpen={showCategoryPicker}
        onClose={() => setShowCategoryPicker(false)}
      >
        <div className="grid grid-cols-3 gap-3 p-4">
          {filteredCategories.map(cat => {
            const Icon = iconMap[cat.icon] || Circle
            const isSelected = formData.categoria_principal === cat.id
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => {
                  triggerHaptic()
                  setFormData(prev => ({ ...prev, categoria_principal: cat.id, subcategoria_id: '' }))
                  setErrors(prev => ({ ...prev, categoria: null }))
                  setShowCategoryPicker(false)
                }}
                className={cn(
                  "flex flex-col items-center gap-2 p-3 rounded-xl border transition-all",
                  isSelected ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                )}
              >
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${cat.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: cat.color }} />
                </div>
                <span className="text-xs font-medium text-center leading-tight">{cat.name}</span>
              </button>
            )
          })}
        </div>
      </PickerSheet>

      {/* Subcategory Picker Sheet */}
      <PickerSheet
        title={locale === 'es-MX' ? 'Selecciona subcategoría' : 'Select subcategory'}
        isOpen={showSubcategoryPicker}
        onClose={() => setShowSubcategoryPicker(false)}
      >
        <div className="space-y-1 p-4">
          {/* Option to clear subcategory */}
          <button
            type="button"
            onClick={() => {
              triggerHaptic()
              setFormData(prev => ({ ...prev, subcategoria_id: '' }))
              setShowSubcategoryPicker(false)
            }}
            className={cn(
              "w-full flex items-center gap-3 p-3 rounded-xl border transition-all",
              !formData.subcategoria_id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
            )}
          >
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
              <Circle className="w-5 h-5 text-muted-foreground" />
            </div>
            <span className="font-medium">
              {locale === 'es-MX' ? 'Sin subcategoría' : 'No subcategory'}
            </span>
          </button>
          
          {subcategories.map(sub => {
            const isSelected = formData.subcategoria_id === sub.id
            return (
              <button
                key={sub.id}
                type="button"
                onClick={() => {
                  triggerHaptic()
                  setFormData(prev => ({ ...prev, subcategoria_id: sub.id }))
                  setShowSubcategoryPicker(false)
                }}
                className={cn(
                  "w-full flex items-center gap-3 p-3 rounded-xl border transition-all",
                  isSelected ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                )}
              >
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: selectedCategory?.color ? `${selectedCategory.color}20` : '#f4f4f5' }}
                >
                  <span style={{ color: selectedCategory?.color || '#666' }} className="text-sm font-bold">
                    {sub.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="font-medium">{sub.name}</span>
              </button>
            )
          })}
        </div>
      </PickerSheet>

      {/* Currency Picker Sheet */}
      <PickerSheet
        title="Selecciona moneda"
        isOpen={showCurrencyPicker}
        onClose={() => setShowCurrencyPicker(false)}
      >
        <div className="grid grid-cols-4 gap-2 p-4">
          {CURRENCIES.map(curr => (
            <button
              key={curr}
              type="button"
              onClick={() => {
                triggerHaptic()
                setFormData(prev => ({ ...prev, moneda: curr }))
                setShowCurrencyPicker(false)
              }}
              className={cn(
                "p-3 rounded-lg border text-center font-medium transition-all",
                formData.moneda === curr ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
              )}
            >
              {curr}
            </button>
          ))}
        </div>
      </PickerSheet>

      {/* Payment Method Picker Sheet */}
      <PickerSheet
        title={t('finance.paymentMethod')}
        isOpen={showMethodPicker}
        onClose={() => setShowMethodPicker(false)}
      >
        <div className="p-4 space-y-2">
          {PAYMENT_METHODS.map(method => (
            <button
              key={method.value}
              type="button"
              onClick={() => {
                triggerHaptic()
                setFormData(prev => ({ ...prev, metodo: method.value }))
                setShowMethodPicker(false)
              }}
              className={cn(
                "w-full p-4 rounded-lg border text-left font-medium transition-all",
                formData.metodo === method.value ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
              )}
            >
              {method.label}
            </button>
          ))}
        </div>
      </PickerSheet>

      {/* MSI Months Picker Sheet */}
      <PickerSheet
        title={locale === 'es-MX' ? 'Número de meses MSI' : 'Number of MSI months'}
        isOpen={showMSIMonthsPicker}
        onClose={() => setShowMSIMonthsPicker(false)}
      >
        <div className="grid grid-cols-3 gap-3 p-4">
          {MSI_MONTHS_OPTIONS.map(months => (
            <button
              key={months}
              type="button"
              onClick={() => {
                triggerHaptic()
                setFormData(prev => ({ ...prev, msi_meses: months }))
                setShowMSIMonthsPicker(false)
              }}
              className={cn(
                "p-4 rounded-lg border text-center font-bold transition-all",
                formData.msi_meses === months ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"
              )}
            >
              {months} meses
            </button>
          ))}
        </div>
        {/* Custom months input */}
        <div className="px-4 pb-4">
          <Label className="text-xs text-muted-foreground mb-1 block">Otro valor (2-36)</Label>
          <Input
            type="number"
            min="2"
            max="36"
            placeholder="Ej: 15"
            className="h-11"
            onChange={(e) => {
              const val = parseInt(e.target.value)
              if (val >= 2 && val <= 36) {
                triggerHaptic()
                setFormData(prev => ({ ...prev, msi_meses: val }))
                setShowMSIMonthsPicker(false)
              }
            }}
          />
        </div>
      </PickerSheet>
    </>
  )
}
